// src/utils/timezone.js
// Timezone helpers.
//
// Danielle (the instructor) lives in Central Time (WI). Cohort meeting times
// are stored as a Chicago-local wall clock: `{ date: "YYYY-MM-DD", time: "HH:MM" }`.
// To show a viewer-local meeting time (Pacific user sees PT, Eastern sees ET, etc.)
// we need to translate "10:00 in Chicago on 2026-04-20" into the true UTC instant,
// then let `toLocaleString` render it in the viewer's tz.
//
// Do NOT store cohort meetings as ISO UTC and then try to infer Chicago back out —
// the admin UI edits a Chicago wall clock, and DST-boundary days would drift
// every time an admin re-saved.

export const TRAINER_TIMEZONE = 'America/Chicago';

// Build a true UTC Date from a trainer-timezone wall clock.
// dateStr: "YYYY-MM-DD", timeStr: "HH:MM" (24h). Handles DST automatically
// via Intl.DateTimeFormat lookup in the target zone.
export function trainerWallClockToUTC(dateStr, timeStr) {
    if (!dateStr) return null;
    const [y, mo, d] = dateStr.split('-').map(Number);
    const [hh, mm] = (timeStr || '00:00').split(':').map(Number);
    if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;

    // First guess: treat the wall clock as if it were UTC. Then ask Intl what
    // that UTC instant looks like in Chicago — the delta between target and
    // displayed wall clock is the Chicago offset we need to add/subtract.
    const guessMs = Date.UTC(y, mo - 1, d, hh, mm, 0);
    const fmt = new Intl.DateTimeFormat('en-US', {
        timeZone: TRAINER_TIMEZONE,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
    });
    const parts = fmt.formatToParts(new Date(guessMs));
    const get = (type) => {
        const p = parts.find((x) => x.type === type);
        return p ? Number(p.value) : NaN;
    };
    let chH = get('hour');
    if (chH === 24) chH = 0; // Intl quirk — midnight can come back as 24
    const chicagoMs = Date.UTC(get('year'), get('month') - 1, get('day'), chH, get('minute'), 0);
    const offset = guessMs - chicagoMs;
    return new Date(guessMs + offset);
}

// Format a JS Date as the viewer's local date label.
// Example: "Monday, April 20"
export function formatViewerLocalDate(date) {
    if (!date) return '';
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

// Format a JS Date as the viewer's local time label.
// Example: "10:00 AM"
export function formatViewerLocalTime(date) {
    if (!date) return '';
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

// Day key (YYYY-MM-DD) for an instant in the trainer's timezone (America/Chicago).
// This is the canonical bucket for streaks + challenge counting: a completion at
// 11pm CT belongs to that CT calendar day regardless of the viewer's device tz,
// so a traveling member or a skewed device clock can't shift which day it counts.
// Accepts a Date, epoch-ms, or ISO string. Returns '' on invalid input.
//
// en-CA formats as YYYY-MM-DD natively, which also sorts lexicographically =
// chronologically — relied on by the streak/week/month math in firebase.js.
export function trainerDayKey(input = Date.now()) {
    const date = input instanceof Date ? input : new Date(input);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: TRAINER_TIMEZONE,
        year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(date);
}

// The calendar day immediately before a YYYY-MM-DD key. Pure string/calendar
// arithmetic (anchored at noon UTC to dodge any DST edge) — timezone-agnostic
// because it operates on the already-resolved CT day key, not on an instant.
export function previousDayKey(key) {
    if (!key || typeof key !== 'string') return '';
    const [y, m, d] = key.split('-').map(Number);
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return '';
    const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
    dt.setUTCDate(dt.getUTCDate() - 1);
    const yy = dt.getUTCFullYear();
    const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(dt.getUTCDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
}

// Friendly "when" label for a past instant — "today", "yesterday", "3 days ago",
// "last week", or a short date for older. Viewer-local is intentional here: this
// is a soft history hint ("last done 3 days ago"), NOT a streak boundary (those
// use trainerDayKey). Returns '' on invalid input.
export function relativeDayLabel(input) {
    if (!input) return '';
    const then = input instanceof Date ? input : new Date(input);
    if (Number.isNaN(then.getTime())) return '';
    const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const days = Math.round((startOfDay(new Date()) - startOfDay(then)) / 86400000);
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 14) return 'last week';
    return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Is `date` in the viewer's local "today"?
export function isViewerLocalToday(date, now = new Date()) {
    if (!date) return false;
    return date.getFullYear() === now.getFullYear()
        && date.getMonth() === now.getMonth()
        && date.getDate() === now.getDate();
}
